import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Home, Calendar, Clock, Globe, Users, BarChart, Check, X, Trash2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import './ArchivePage.css';

const ArchivePage = () => {
  const navigate = useNavigate();
  const [debates, setDebates] = useState([]);
  const [filteredDebates, setFilteredDebates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLanguage, setSelectedLanguage] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [isAdmin, setIsAdmin] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    // Fetch user data from API to verify admin status (more secure than localStorage)
    const checkAdminStatus = async () => {
      const token = localStorage.getItem('user_session_token') || 
                    localStorage.getItem('admin_session_token') || 
                    localStorage.getItem('token');
      
      if (token) {
        try {
          const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.ok) {
            const user = await response.json();
            setIsAdmin(user.is_admin === true);
          }
        } catch (e) {
          console.error('Error checking admin status:', e);
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    
    checkAdminStatus();
    fetchArchive();
  }, []);

  useEffect(() => {
    filterAndSortDebates();
  }, [debates, selectedLanguage, sortBy]);

  const fetchArchive = async () => {
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('user_session_token');
      
      if (!token) {
        // Not authenticated - redirect to login
        navigate('/login');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/archive/filtered`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        // Token invalid or expired - redirect to login
        navigate('/login');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch archive');
      }

      const data = await response.json();
      setDebates(data);
    } catch (error) {
      console.error('Error fetching archive:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortDebates = () => {
    let filtered = [...debates];

    // Filter by language
    if (selectedLanguage !== 'all') {
      filtered = filtered.filter(d => d.language === selectedLanguage);
    }

    // Sort
    if (sortBy === 'date') {
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (sortBy === 'duration') {
      filtered.sort((a, b) => (b.duration_minutes || 0) - (a.duration_minutes || 0));
    }

    setFilteredDebates(filtered);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getLanguageName = (code) => {
    const languages = {
      'de': 'Deutsch',
      'en': 'English',
      'es': 'Español',
      'fr': 'Français',
      'it': 'Italiano'
    };
    return languages[code] || code;
  };

  const getStatusBadge = (debate) => {
    if (debate.status === 'completed') {
      return <span className="status-badge completed"><Check size={14} /> Abgeschlossen</span>;
    } else if (debate.status === 'aborted') {
      return <span className="status-badge aborted"><X size={14} /> Abgebrochen</span>;
    }
    return null;
  };

  const handleDeleteDebate = async (debateId, event) => {
    // Stop propagation to prevent navigation to debate details
    event.stopPropagation();
    
    if (!window.confirm('Bist du sicher, dass du diese Debatte löschen möchtest? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('user_session_token');
      
      const response = await fetch(`${BACKEND_URL}/api/archive/${debateId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete debate');
      }
      
      // Remove from local state
      setDebates(prevDebates => prevDebates.filter(d => d.id !== debateId));
      toast.success('Debate deleted successfully');
      
    } catch (error) {
      console.error('Error deleting debate:', error);
      toast.error('Error deleting debate');
    }
  };

  const handleLogout = () => {
    // Clear all localStorage data
    localStorage.clear();
    
    // Show success message
    toast.success('Successfully logged out');
    
    // Navigate to landing page
    navigate('/');
  };

  const getScoreDisplay = (debate) => {
    const scoreA = debate.total_score_a || 0;
    const scoreB = debate.total_score_b || 0;
    return `${scoreA}:${scoreB}`;
  };

  if (loading) {
    return (
      <div className="archive-page">
        <div className="archive-loading">Lade Archiv...</div>
      </div>
    );
  };

  return (
    <div className="archive-page">
      <div className="archive-container">
        {/* Header */}
        <div className="archive-header">
          <div className="header-left">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="home-button"
            >
              <Home className="mr-2 h-4 w-4" />
              Home
            </Button>
          </div>
          
          <h1>Debate Archive</h1>
          
          <div className="header-right">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="archive-filters">
          <div className="filter-group">
            <label>Language:</label>
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="filter-select"
            >
              <option value="all">All languages</option>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="fr">Français</option>
              <option value="it">Italiano</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Sortierung:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="date">Newest first</option>
              <option value="duration">Longest first</option>
            </select>
          </div>

          <div className="debate-count">
            {filteredDebates.length} {filteredDebates.length === 1 ? 'debate' : 'debates'}
          </div>
        </div>

        {/* Debate Cards */}
        <div className="archive-list">
          {filteredDebates.length === 0 ? (
            <div className="no-debates">
              <p>No debates found in archive.</p>
            </div>
          ) : (
            filteredDebates.map(debate => (
              <Card key={debate.id} className="debate-card">
                <CardContent className="debate-card-content">
                  {/* Thesis - Large & Bold */}
                  <h2 className="debate-thesis">{debate.thesis}</h2>

                  {/* Metadata Row */}
                  <div className="debate-metadata">
                    <div className="metadata-item">
                      <Users size={16} />
                      <span>{debate.participant_a} vs {debate.participant_b}</span>
                    </div>

                    <div className="metadata-item">
                      <Calendar size={16} />
                      <span>{formatDate(debate.created_at)}</span>
                    </div>

                    <div className="metadata-item">
                      <Clock size={16} />
                      <span>{formatTime(debate.created_at)}</span>
                    </div>

                    <div className="metadata-item">
                      <Globe size={16} />
                      <span>{getLanguageName(debate.language)}</span>
                    </div>

                    <div className="metadata-item">
                      <BarChart size={16} />
                      <span>Score: {getScoreDisplay(debate)}</span>
                    </div>

                    {debate.duration_minutes && (
                      <div className="metadata-item">
                        <Clock size={16} />
                        <span>{debate.duration_minutes} Min.</span>
                      </div>
                    )}

                    {getStatusBadge(debate)}
                  </div>

                  {/* Action Buttons */}
                  <div className="debate-actions">
                    <Button
                      className="view-debate-button"
                      onClick={() => navigate(`/archive/view/${debate.id}`)}
                    >
                      View debate →
                    </Button>
                    
                    {/* Admin Delete Button */}
                    {isAdmin && (
                      <button
                        className="delete-debate-button"
                        onClick={(e) => handleDeleteDebate(debate.id, e)}
                        title="Delete debate (Admin)"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ArchivePage;
